import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send, ExternalLink, Facebook, Instagram, RefreshCw } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { AR_STYLE } from "@/components/creatives/ChatMessage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/creatives`;

export const PublishDialog = ({ asset, onClose, onDone }) => {
  const igBlocked = asset.kind === "image" && asset.aspect_ratio === "9:16";
  const [caption, setCaption] = useState("");
  const [loading, setLoading] = useState(true);
  const [pf, setPf] = useState({ facebook: true, instagram: !igBlocked });
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);

  const write = () => {
    setLoading(true);
    axios.post(`${API}/${asset.id}/caption`).then((r) => setCaption(r.data.caption)).catch((e) => toast.error(e.response?.data?.detail || e.message)).finally(() => setLoading(false));
  };
  useEffect(write, [asset.id]);

  const go = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/${asset.id}/publish`, { caption, platforms: Object.keys(pf).filter((k) => pf[k]) }, { timeout: 400000 });
      setResults(r.data.results);
      const ok = Object.values(r.data.results).filter((x) => x.posted).length;
      ok ? toast.success(`Publicat pe ${ok} platform${ok > 1 ? "e" : "ă"}`) : toast.error("Publicarea a eșuat");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setBusy(false);
  };

  const Toggle = ({ k, icon: Icon, label, disabled }) => (
    <button data-testid={`publish-platform-${k}`} disabled={disabled} onClick={() => setPf({ ...pf, [k]: !pf[k] })} className={`flex flex-1 items-center justify-center gap-2 rounded-full border py-2 text-sm disabled:opacity-40 ${pf[k] ? "border-amber-500 bg-amber-100 text-amber-900" : "border-zinc-300 text-zinc-500"}`}>
      <Icon className="h-4 w-4" /> {label}
    </button>
  );

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="publish-dialog" className="max-h-[92vh] max-w-lg overflow-y-auto">
        <DialogHeader><DialogTitle>Publică pe paginile BRAGUS</DialogTitle></DialogHeader>
        {asset.kind === "video"
          ? <video src={asset.url} controls muted style={{ aspectRatio: AR_STYLE[asset.aspect_ratio] }} className="mx-auto max-h-72 rounded-lg bg-black" />
          : <img src={asset.url} alt="" style={{ aspectRatio: AR_STYLE[asset.aspect_ratio] }} className="mx-auto max-h-72 rounded-lg object-cover" />}
        <div className="relative">
          <Textarea data-testid="publish-caption-input" value={caption} onChange={(e) => setCaption(e.target.value)} rows={7} className="pb-9 pr-10" placeholder={loading ? "AI-ul scrie textul postării…" : "Textul postării"} disabled={loading} />
          <button data-testid="publish-rewrite-btn" onClick={write} disabled={loading} title="Rescrie cu AI" className="absolute bottom-2 right-2 rounded-full bg-zinc-800 p-1.5 text-zinc-300 hover:text-amber-400">
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          </button>
        </div>
        <div className="flex gap-2">
          <Toggle k="facebook" icon={Facebook} label="Facebook" />
          <Toggle k="instagram" icon={Instagram} label={asset.kind === "video" ? "Instagram Reel" : "Instagram"} disabled={igBlocked} />
        </div>
        {igBlocked && <p className="text-xs text-zinc-500">Instagram nu acceptă imagini 9:16 în feed. Refă-o în 1:1 sau 4:5 pentru Instagram.</p>}
        {results && (
          <div className="space-y-1 text-sm" data-testid="publish-results">
            {Object.entries(results).map(([k, r]) => (
              <p key={k} className={r.posted ? "text-emerald-700" : "text-rose-700"}>
                {k === "facebook" ? "Facebook" : "Instagram"}: {r.posted ? "publicat " : `eșuat — ${r.error}`}
                {r.posted && r.url && <a href={r.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 underline">vezi postarea <ExternalLink className="h-3 w-3" /></a>}
              </p>
            ))}
          </div>
        )}
        <button data-testid="publish-confirm-btn" onClick={go} disabled={busy || loading || !caption.trim() || !Object.values(pf).some(Boolean)} className="flex items-center justify-center gap-2 rounded-full bg-amber-400 py-2.5 text-sm font-semibold text-zinc-950 disabled:opacity-50">
          {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> {asset.kind === "video" ? "Se publică (video-ul poate dura câteva minute)…" : "Se publică…"}</> : <><Send className="h-4 w-4" /> Publică acum</>}
        </button>
      </DialogContent>
    </Dialog>
  );
};
